import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/styles/webawesome.css';
import '../components/breadcrumb.js';
import '../components/empty_state.js';
import '../components/icon.js';

import { faPlus, faSeedling } from '@fortawesome/free-solid-svg-icons';
import { html, LitElement, nothing, type TemplateResult } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import {
    type IFarmFacets,
    type IFarmListItem,
    listAccessibleFarms,
} from '../libraries/api_client/farm.js';
import {
    farmFilterHeading,
    farmFilters,
    farmRestrictionQuery,
    farmSearch,
    hiddenInactiveFarmsCopy,
    noFarmsYetCopy,
} from '../libraries/farm_copy.js';
import { inactiveHiddenHint } from '../libraries/inactive_hint.js';
import { legalFormLabel } from '../libraries/legal_form_options.js';
import {
    type IListResult,
    type IListState,
    ListController,
    listHostStyles,
    listResultFrom,
} from '../libraries/list_controller.js';
import { type IListIdentity, listIds } from '../libraries/list_identity.js';
import type { IListFilter } from '../libraries/list_restriction.js';
import type { IListColumn } from '../libraries/list_table.js';
import { redirectTo } from '../libraries/navigation.js';
import { noMatchesHint } from '../libraries/no_matches_hint.js';
import { entityStatusBadge } from '../libraries/status_badge.js';

const farmPageSize = 25;

const farmColumns: Array<IListColumn<IFarmListItem>> = [
    {
        label: 'Name',
        track: 'm',
        cell: farm => farm.name,
    },
    {
        label: 'Rechtsform',
        cell: farm => legalFormLabel(farm.legalForm),
    },
    {
        label: 'Ort',
        track: 'xs',
        cell: farm => farm.city,
    },
    {
        label: 'Status',
        track: 'xs',
        cell: farm => entityStatusBadge(farm.active),
    },
];

/**
 * "Alle Betriebe" page rendered at `/farms` — the "Alle" scope.
 *
 * Lists every farm the caller can access one page at a time: orphan farms they
 * own plus every farm in every organisation they are a member of. Org-scoped
 * farms link into their canonical `/organizations/:orgId/farms/:farmId` URL;
 * orphan farms link to `/farms/:farmId`. Users with zero accessible farms see
 * the "Noch kein Betrieb angelegt" empty-state.
 */
@customElement('fm-farm-list')
export class FmFarmList extends LitElement {

    static styles = listHostStyles;

    /**
     * The values the filters may narrow to, as the last load reported them. Held
     * here rather than read from the list, because the declaration is built from
     * it and a controller cannot hand a value to the thunk that declares it.
     */
    @state()
    private facets: IFarmFacets | undefined = undefined;

    private readonly list = new ListController<IFarmListItem, IFarmFacets | undefined>(this, {
        label: 'Betriebe',
        columns: (): Array<IListColumn<IFarmListItem>> => farmColumns,
        rowKey: (farm): string => farm.id,
        rowHref: (farm): string => this.farmHref(farm),
        withChevron: true,
        rowClass: (farm): string => (farm.active ? '' : 'row-inactive'),
        pageSize: farmPageSize,
        identity: (): IListIdentity => ({
            id: listIds.farms,
            scope: null,
        }),
        trackPageInUrl: true,
        readsDisplayFilter: true,
        search: farmSearch,
        filterHeading: farmFilterHeading,
        filters: (): ReadonlyArray<IListFilter> => farmFilters(this.facets),
        errorDescription: 'Die Betriebe konnten nicht geladen werden.',
        load: async ({
            offset, limit, hideInactive, search, filters,
        }): Promise<IListResult<IFarmListItem, IFarmFacets | undefined>> => listResultFrom(
            await listAccessibleFarms({
                offset,
                limit,
                hideInactive,
                search,
                facets: this.facets === undefined,
                ...farmRestrictionQuery(filters),
            }),
            page => ({
                rows: page.items,
                unfilteredTotal: page.unfilteredTotal,
                meta: page.facets,
                page: {
                    total: page.total,
                    offset: page.offset,
                    limit: page.limit,
                },
            })
        ),
        empty: (listState): TemplateResult => this.renderEmptyBody(listState),
        actions: (listState): TemplateResult | typeof nothing => this.renderCreateAction(listState),
        onLoaded: ({ meta }): void => { this.facets = meta ?? this.facets; },
    });

    private readonly handleCreate = (): void => {
        redirectTo('/farms/new');
    };

    private farmHref (farm: IFarmListItem): string {
        return farm.managingOrganizationId === null ?
            `/farms/${farm.id}` :
            `/organizations/${farm.managingOrganizationId}/farms/${farm.id}`;
    }

    private renderCreateButton (slot: string): TemplateResult {
        return html`
            <wa-button slot=${slot} size="s" variant="brand" @click=${this.handleCreate}>
                <fm-icon slot="start" .icon=${faPlus}></fm-icon>
                Betrieb anlegen
            </wa-button>
        `;
    }

    /**
     * The heading row's CTA. Left out only while the "Noch kein Betrieb angelegt"
     * card is the body, because that card carries one itself. A set the display
     * filter emptied still gets it, since that card only links to the settings,
     * and so does a restriction that matched nothing: the way out of that one is
     * the reset, and taking the CTA away would leave a caller who searched before
     * creating their first farm with nothing to click.
     * @param listState - What the list currently holds.
     * @returns The button, or nothing.
     */
    private renderCreateAction (listState: IListState<IFarmListItem>): TemplateResult | typeof nothing {
        const showsEmptyStateCard = !listState.restricted && listState.unfilteredTotal === 0;

        return showsEmptyStateCard ? nothing : this.renderCreateButton('actions');
    }

    private renderEmptyState (): TemplateResult {
        return html`
            <fm-empty-state
                .icon=${faSeedling}
                heading=${noFarmsYetCopy.heading}
                description=${noFarmsYetCopy.description}>
                ${this.renderCreateButton('action')}
            </fm-empty-state>
        `;
    }

    /**
     * The body while the table holds no rows, of which there are three, and the
     * display preference has to be tested first: `unfilteredTotal` counts the
     * same restriction minus the preference, so anything left there is a row the
     * restriction matched and the setting then hid, which makes the setting the
     * cause and the reset no help at all.
     * @param listState - What the list currently holds.
     * @returns The empty body.
     */
    private renderEmptyBody (listState: IListState<IFarmListItem>): TemplateResult {
        if (listState.unfilteredTotal > 0) {
            return inactiveHiddenHint(
                hiddenInactiveFarmsCopy.heading,
                hiddenInactiveFarmsCopy.description
            );
        }

        return listState.restricted ?
            noMatchesHint(this.list.resetRestriction) :
            this.renderEmptyState();
    }

    render (): TemplateResult {
        return this.list.render(html`
            <fm-breadcrumb slot="breadcrumb" .items=${[
                {
                    label: 'Alle',
                },
            ]}></fm-breadcrumb>
        `);
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fm-farm-list': FmFarmList;
    }
}
